/**
 * useBorrowings — Hook para gestionar préstamos P2P del usuario.
 *
 * Muestra los préstamos donde el usuario es borrower u owner.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface Borrowing {
  id: string;
  borrower_id: string;
  owner_id: string;
  set_id: string;
  user_set_id: string;
  status: "requested" | "accepted" | "shipping" | "active" | "returning" | "completed" | "cancelled" | "disputed";
  brickpoints_cost: number;
  brickpoints_per_week: number;
  estimated_weeks: number;
  weeks_used: number;
  piece_count: number | null;
  max_duration_weeks: number | null;
  tier: "standard" | "master_builder" | null;
  delivery_method: "hand" | "shipping" | null;
  return_deadline_at: string | null;
  due_at: string | null;
  extension_status: "none" | "requested" | "approved" | "rejected" | "expired" | null;
  extension_requested_at: string | null;
  extension_responded_at: string | null;
  extension_bp_cost: number | null;
  deposit_amount_cents: number | null;
  deposit_status: "none" | "held" | "released" | "captured" | null;
  requested_at: string;
  accepted_at: string | null;
  delivered_at: string | null;
  returned_at: string | null;
  completed_at: string | null;
  disputed_at?: string | null;
  disputed_by?: string | null;
  dispute_reason?: string | null;
  dispute_status?: "none" | "open" | "legitimate" | "dismissed" | null;
  dispute_resolution_notes?: string | null;
  dispute_resolved_at?: string | null;
  dispute_resolved_by?: string | null;
  created_at: string;
  sets?: {
    set_name: string;
    set_ref: string;
    set_image_url: string;
    set_piece_count: number;
    set_price?: number | null;
    instructions?: Array<{ url: string; description?: string | null }> | null;
  } | null;
  borrower?: { email: string; full_name: string } | null;
  owner?: { email: string; full_name: string } | null;
}

export const BORROWING_STATUS_LABEL: Record<Borrowing["status"], string> = {
  requested: "Solicitado",
  accepted: "Aceptado",
  shipping: "En envío",
  active: "En préstamo",
  returning: "Devolviendo",
  completed: "Completado",
  cancelled: "Cancelado",
  disputed: "En disputa",
};

export const BORROWING_STATUS_COLOR: Record<Borrowing["status"], string> = {
  requested: "bg-yellow-100 text-yellow-800",
  accepted: "bg-blue-100 text-blue-800",
  shipping: "bg-purple-100 text-purple-800",
  active: "bg-green-100 text-green-800",
  returning: "bg-orange-100 text-orange-800",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-800",
  disputed: "bg-pink-100 text-pink-800",
};

export function useBorrowings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["borrowings", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<Borrowing[]> => {
      const { data, error } = await supabase
        .from("borrowings")
        .select(`
          *,
          sets:set_id ( set_name, set_ref, set_image_url, set_piece_count, set_price, instructions ),
          borrower:borrower_id ( email, full_name ),
          owner:owner_id ( email, full_name )
        `)
        .or(`borrower_id.eq.${user!.id},owner_id.eq.${user!.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as Borrowing[];
    },
  });
}

export function useAcceptBorrowing() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "accept" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al aceptar préstamo");
      }

      toast.success("✅ Préstamo aceptado. El set está RESERVADO.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error("Error: " + e.message);
    }
  };
}

export function useRejectBorrowing() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "reject" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al rechazar préstamo");
      }

      toast.success("✅ Solicitud rechazada correctamente.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error("Error: " + e.message);
    }
  };
}

export function useConfirmDelivery() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "deliver" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al confirmar entrega");
      }

      toast.success("✅ Set ENTREGADO. El préstamo ha comenzado.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error("Error: " + e.message);
    }
  };
}

export function useCancelBorrowing() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "cancel" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al cancelar préstamo");
      }

      toast.success("✅ Solicitud cancelada.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error("Error: " + e.message);
    }
  };
}

export function useRequestReturn() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "request_return" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al iniciar devolución");
      }

      toast.success("✅ Devolución iniciada. Espera confirmación del propietario.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["borrowing_history"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error("Error: " + e.message);
    }
  };
}

export function useConfirmReturn() {
  const queryClient = useQueryClient();

  return async (
    borrowingId: string,
    feedback: { rating: number; tags?: string[]; comment?: string }
  ) => {
    try {
      if (!feedback || typeof feedback.rating !== "number" || feedback.rating < 1 || feedback.rating > 5) {
        throw new Error("La valoración de reputación (1 a 5 estrellas) es obligatoria.");
      }

      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: {
          borrowing_id: borrowingId,
          action: "confirm_return",
          rating: feedback.rating,
          tags: feedback.tags,
          comment: feedback.comment,
        },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al confirmar devolución");
      }

      toast.success("✅ Devolución confirmada. El set está disponible nuevamente.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["borrowing_history"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
      queryClient.invalidateQueries({ queryKey: ["community_pool"] });
      queryClient.invalidateQueries({ queryKey: ["user_reputation"] });
      queryClient.invalidateQueries({ queryKey: ["user_reputation_feed"] });
    } catch (e: any) {
      toast.error("Error: " + e.message);
      throw e;
    }
  };
}

export function useRequestExtension() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "request_extension" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al solicitar prórroga");
      }

      toast.success("✅ Prórroga solicitada. El propietario tiene 48h para responder.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error(e.message);
      throw e;
    }
  };
}

export function useApproveExtension() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "approve_extension" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al aprobar prórroga");
      }

      toast.success("✅ Prórroga de 2 semanas aprobada correctamente.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
      queryClient.invalidateQueries({ queryKey: ["user_profile"] });
    } catch (e: any) {
      toast.error(e.message);
      throw e;
    }
  };
}

export function useRejectExtension() {
  const queryClient = useQueryClient();

  return async (borrowingId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-borrowing", {
        body: { borrowing_id: borrowingId, action: "reject_extension" },
      });

      if (error || data?.success === false) {
        throw new Error(data?.error ?? error?.message ?? "Error al rechazar prórroga");
      }

      toast.info("Prórroga rechazada. Se ha avisado al prestatario para tramitar la devolución.");
      queryClient.invalidateQueries({ queryKey: ["borrowings"] });
      queryClient.invalidateQueries({ queryKey: ["user_sets"] });
    } catch (e: any) {
      toast.error(e.message);
      throw e;
    }
  };
}

